/**
 * In-process mock backend.
 *
 * `src/lib/api.ts` calls these directly when NEXT_PUBLIC_USE_MOCK_API is on,
 * so the same handlers serve both `getServerSideProps` (server) and client-side
 * fetches without a second process. The same functions are also exposed over
 * HTTP at `/api/mock/*` if you want to point another client at them.
 *
 * Orders live in memory and reset when the server restarts.
 */

import { calculateOrderTotals } from "@/utils/pricing";
import seedJson from "@/data/seed.json";
import {
  ORDER_STATUS_ENUM,
  PAYMENT_METHOD_ENUM,
  type OrderStatus,
  type OrderType,
} from "@/constants/enums";
import type {
  Delivery,
  DeliveryArea,
  MenuResponse,
  Order,
  OrderPayload,
  Rating,
  RatingPayload,
  DineinFloor,
  StoreData,
  StoreMetadata,
} from "@/types";
import type { GiftCard, Offer, Voucher } from "@/types/wallet";
import {
  deliveryAreas,
  giftCards,
  offers,
  ratings,
  resolveCategories,
  resolveItems,
  dineinLayout,
  vouchers,
} from "./menu";

const seed = seedJson as unknown as {
  data: StoreData;
  metadata: StoreMetadata;
};

interface MockStore {
  orders: Map<string, Order>;
  deliveries: Map<string, Delivery>;
  orderCounter: number;
}

declare global {
  // eslint-disable-next-line no-var
  var __eposmaticMockStore: MockStore | undefined;
}

/**
 * Next.js bundles each page and API route separately on the server, so a plain
 * module-level Map would not be shared between `/api/mock/*` and a page's
 * `getServerSideProps`. Hanging the store off `globalThis` keeps one instance
 * per Node process (and survives dev-mode hot reloads).
 */
const store: MockStore = (globalThis.__eposmaticMockStore ??= {
  orders: new Map<string, Order>(),
  deliveries: new Map<string, Delivery>(),
  orderCounter: 1000,
});

const { orders, deliveries } = store;

const ORDER_PROGRESSION: OrderStatus[] = [
  ORDER_STATUS_ENUM.PLACED,
  ORDER_STATUS_ENUM.PREPARING,
  ORDER_STATUS_ENUM.READY,
  ORDER_STATUS_ENUM.COMPLETED,
];

/**
 * Seconds after placement at which the order enters each stage.
 *
 * `MOCK_ORDER_STAGE_SECONDS=0,1,2,3` compresses the progression so the later
 * states — and the "Rate your order" prompt that only appears on a completed
 * order — can be exercised in a test without waiting two minutes.
 */
const parseStageSeconds = (raw: string | undefined): number[] => {
  const parsed = (raw ?? "")
    .split(",")
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isFinite(value));
  return parsed.length === ORDER_PROGRESSION.length ? parsed : [0, 20, 60, 120];
};

const STAGE_AFTER_SECONDS = parseStageSeconds(
  process.env.MOCK_ORDER_STAGE_SECONDS,
);

const statusForAge = (placedAt: string): OrderStatus => {
  const ageSeconds = (Date.now() - new Date(placedAt).getTime()) / 1000;
  let status = ORDER_PROGRESSION[0] as OrderStatus;
  STAGE_AFTER_SECONDS.forEach((threshold, index) => {
    if (ageSeconds >= threshold)
      status = ORDER_PROGRESSION[index] as OrderStatus;
  });
  return status;
};

const delay = (ms = 120) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const getStoreData = async (): Promise<StoreData> => {
  await delay();
  return seed.data;
};

export const getStoreMetadata = async (): Promise<StoreMetadata> => {
  await delay();
  return seed.metadata;
};

export const getRatings = async (): Promise<Rating[]> => {
  await delay();
  return ratings;
};

export const getMenu = async (_options?: {
  orderType?: OrderType;
}): Promise<MenuResponse> => {
  await delay();
  const settings = seed.data.settings ?? {};
  // The tenant decides whether a featured category exists, and what it is
  // called. This used to inject "Popular" unconditionally, inventing a
  // category the back-office had switched off.
  const showFeatured = settings.web_show_featured_items !== false;
  const featuredName = settings.featured_category_name ?? "Popular";
  return {
    categories: resolveCategories(showFeatured, featuredName),
    items: resolveItems(showFeatured),
  };
};

export const getOffers = async (): Promise<Offer[]> => {
  await delay();
  return offers;
};

export const getVouchers = async (): Promise<Voucher[]> => {
  await delay();
  return vouchers;
};

export const getGiftCards = async (): Promise<GiftCard[]> => {
  await delay();
  return giftCards;
};

export const getDeliveryAreas = async (): Promise<DeliveryArea[]> => {
  await delay();
  return deliveryAreas;
};

export const getDineinLayout = async (): Promise<DineinFloor[]> => {
  await delay();
  return dineinLayout;
};

/** Most recent orders first — used by the "Recent orders" drawer. */
export const getRecentOrders = async (): Promise<Order[]> => {
  await delay();
  return [...orders.values()]
    .map((order) => ({ ...order, status: statusForAge(order.placed_at) }))
    .sort(
      (a, b) =>
        new Date(b.placed_at).getTime() - new Date(a.placed_at).getTime(),
    )
    .slice(0, 20);
};

/**
 * Failure injection. A customer named `FAIL` makes the mock reject, so the
 * error path can be exercised in development and asserted in tests without a
 * flaky network.
 */
const shouldSimulateFailure = (payload: OrderPayload): boolean =>
  payload.customer?.name?.trim().toUpperCase() === "FAIL";

export const createOrder = async (payload: OrderPayload): Promise<Order> => {
  await delay(400);

  if (shouldSimulateFailure(payload)) {
    throw new Error("The kitchen could not accept this order right now.");
  }

  // Priced through the same function the basket screen uses, so the estimate
  // and the charge cannot drift apart.
  const totals = calculateOrderTotals({
    items: payload.items ?? [],
    orderType: payload.order_type,
    paymentMethod: payload.payment_method,
    settings: seed.data.settings ?? {},
    deliveryCost: payload.address?.deliveryCost ?? 0,
  });

  store.orderCounter += 1;
  const id = `ord_${store.orderCounter}`;

  const order: Order = {
    _id: id,
    reference: String(store.orderCounter),
    tenant_id: payload.tenant_id,
    order_type: payload.order_type,
    status: ORDER_STATUS_ENUM.PLACED,
    placed_at: new Date().toISOString(),
    items: payload.items ?? [],
    note: payload.note ?? "",
    customer: payload.customer ?? null,
    payment_method: payload.payment_method ?? PAYMENT_METHOD_ENUM.CARD,
    delivery_area: payload.address?.areaName,
    subtotal: totals.subtotal,
    tax: totals.tax,
    packaging: totals.packaging,
    delivery: totals.delivery,
    service_fee: totals.serviceFee,
    surcharge: totals.surcharge,
    total: totals.total,
  };

  orders.set(id, order);

  if (payload.order_type === "DELIVERY") {
    deliveries.set(id, {
      _id: id,
      driver_name: "Your rider is on the way",
      eta: "25 minutes",
      status: "ASSIGNED",
    });
  }

  return order;
};

/**
 * Status is derived from how long ago the order was placed, so it advances on
 * its own as you refresh — and reads stay idempotent.
 */
export const getOrder = async (orderId: string): Promise<Order | null> => {
  await delay();
  const order = orders.get(orderId);
  if (!order) return null;

  order.status = statusForAge(order.placed_at);
  return order;
};

export const getDelivery = async (
  deliveryId: string,
): Promise<Delivery | null> => {
  await delay();
  return deliveries.get(deliveryId) ?? null;
};

export const createRating = async (
  token: string,
  payload: RatingPayload,
): Promise<Rating> => {
  await delay(300);
  const entry: Rating = { _id: `rate_${token}`, ...payload };
  ratings.unshift(entry);
  return entry;
};
